/**
 * "Histórico do perfil" — Google-Docs-style version history for an
 * archived candidate evaluation. Two-pane layout:
 *
 *   ┌──────────┬─────────────────────────────────────┐
 *   │  spine   │            manuscript               │
 *   │  (left)  │            (right pane)             │
 *   └──────────┴─────────────────────────────────────┘
 *
 * The spine is a chronological timeline (newest first) of every CHRO
 * correction made to the evaluation, plus a "Avaliação inicial" entry
 * pinned at the bottom. Click an entry → the manuscript on the right
 * scrolls to the affected row and pulses briefly.
 *
 * The manuscript renders the assessment as an editor's draft. Every
 * row carries strikethrough-old / underline-new markup so the reader
 * can see what changed at a glance. Designed to feel like the
 * margin-annotated draft of an editorial — the corrections are the
 * editor's red ink on a cream-paper page.
 *
 * Data sources:
 *   - GET /api/labs/evaluations/:id           — current assessment
 *   - GET /api/labs/corrections-audit?evaluationId=:id  — corrections
 *
 * No new server routes; both endpoints already exist.
 */

import { isInterviewDecision } from "@shared/labs/three-axis";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Loader2, X } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { rpcClient } from "@/lib/rpc";
import "./profile-history.css";

interface AuditRow {
  id: string;
  evidenceId: string;
  targetKind: string;
  targetRef: string | null;
  candidateName: string | null;
  evaluationId: string | null;
  originalScore: string;
  correctedScore: string;
  rationale: string;
  status: string;
  policy: string;
  correctedBy: string | null;
  correctedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  appliedAt: string | null;
  hasPatch: boolean;
}

interface EvidenceSource {
  quote: string;
  citation: string;
  url?: string;
}

interface SkillBullet {
  id: string;
  text: string;
  level: "L1" | "L2" | "L3" | "L4" | "L5";
  met: boolean;
  kind?: "framework" | "observation";
  source?: EvidenceSource;
  gapRationale?: string;
}

interface EvaluationDetail {
  id: string;
  disciplineSlug: string | null;
  targetLevel: string | null;
  createdBy: string | null;
  createdAt: string;
  assessment: {
    candidateName?: string;
    appliedLevel?: string;
    summary?: string;
    threeAxis?: {
      decision?: string;
      appliedLevel?: string;
      decisionReason?: string;
      axes?: { id: string; value: number; threshold: number; pass: boolean }[];
      evidences?: {
        id: string;
        name?: string;
        score: number | null;
        sources?: EvidenceSource[];
        rubric?: { level: 1 | 2 | 3 | 4 | 5; description: string }[];
        /** LLM's per-candidate framing of the score (the `.q` line on the
         *  candidate page). Falls back to the generic dim description from
         *  DIM_METADATA when no candidate rationale was emitted. */
        description?: string;
      }[];
      frameworkSkills?: {
        id: string;
        name?: string;
        assigned: string;
        assignedDots?: number;
        bullets?: SkillBullet[];
      }[];
    };
  };
}

const DIM_LABEL: Record<string, string> = {
  academic: "Acadêmico",
  trajectory: "Trajetória",
  tenure: "Estabilidade",
  founder: "Sinal de fundador / construtor",
};

const SKILL_LABEL: Record<string, string> = {
  pm: "Project management",
  // 2026-05-19: SM rename — keep id `sm` / `stakeholder_management`,
  // surface the new public label.
  sm: "Agency to influence stakeholders",
  ba: "Business acumen",
  hf: "HR fluency",
  ps: "Product sense",
  project_management: "Project management",
  stakeholder_management: "Agency to influence stakeholders",
  business_acumen: "Business acumen",
  hr_fluency: "HR fluency",
  product_sense: "Product sense",
};

const AXIS_LABEL: Record<string, string> = {
  gca: "GCA",
  compWay: "Comp Way",
  skills: "Skills",
};

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }) +
    " · " +
    d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  );
}

function formatScore(raw: string | number | null | undefined): string {
  if (raw === null || raw === undefined) return "—";
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  return n.toFixed(2).replace(/\.?0+$/, "") || "0";
}

interface CorrectionLabel {
  /** Section heading inside the manuscript, e.g. "Evidências". */
  section: "Eixos" | "Evidências" | "Skills" | "Outro";
  /** The row's display label inside that section. */
  detail: string;
  /** Anchor key for scroll-to behavior — matches the manuscript row's data attribute. */
  anchor: string;
}

/**
 * Build the contextual signal list rendered under each skill row. Mirrors
 * the candidate page's per-skill rubric panel: framework criteria at the
 * candidate's assigned level get paired with the matching observation
 * quote when the LLM tagged one, or surface the gap rationale when the
 * criterion is missed. Loose observations that didn't attach to a
 * criterion fall through as standalone quotes.
 */
function buildSkillSignals(bullets: SkillBullet[], assignedLevel: number): Signal[] {
  const out: Signal[] = [];
  const atLevel = bullets.filter((b) => Number(b.level.slice(1)) === assignedLevel);
  const framework = atLevel.filter((b) => b.kind === "framework");
  const observations = atLevel.filter((b) => b.kind !== "framework");

  // Pair each framework criterion with at most one matching observation
  // — the LLM doesn't link them explicitly, so we match on text overlap.
  const matchedObs = new Set<string>();
  const findObs = (criterion: SkillBullet): SkillBullet | undefined => {
    return observations.find((o) => {
      if (matchedObs.has(o.id)) return false;
      if (o.met !== criterion.met) return false;
      // Loose contains-match — covers the common "criterion: 'Owns X' →
      // observation: '↑ Owns X via …'" pattern without bringing in a
      // proper similarity scorer.
      const c = criterion.text.toLowerCase().split(/\s+/).slice(0, 4).join(" ");
      return c.length > 0 && o.text.toLowerCase().includes(c);
    });
  };

  // The framework set is the primary spine of the panel — emit met
  // criteria first (greens), then unmet (greys with gap rationales).
  const ordered = [...framework].sort((a, b) => Number(b.met) - Number(a.met));
  for (const crit of ordered) {
    const linked = findObs(crit);
    if (linked) matchedObs.add(linked.id);
    out.push({
      kind: "criterion",
      text: crit.text,
      met: crit.met,
      quote: linked?.source?.quote ?? crit.source?.quote,
      citation: linked?.source?.citation ?? crit.source?.citation,
      gap: !crit.met ? (crit.gapRationale ?? linked?.gapRationale) : undefined,
    });
    if (out.length >= 6) break;
  }

  // Then any leftover observation quotes the LLM emitted that didn't
  // line up with a framework criterion — still useful context.
  for (const obs of observations) {
    if (matchedObs.has(obs.id)) continue;
    if (!obs.source) continue;
    out.push({ kind: "quote", quote: obs.source.quote, citation: obs.source.citation });
    if (out.length >= 8) break;
  }

  return out;
}

function labelCorrection(c: AuditRow): CorrectionLabel {
  if (c.targetKind === "dim") {
    return {
      section: "Evidências",
      detail: DIM_LABEL[c.evidenceId] ?? c.evidenceId,
      anchor: `evidence:${c.evidenceId}`,
    };
  }
  if (c.targetKind === "skill_framework" || c.targetKind === "skill_leveling") {
    return {
      section: "Skills",
      detail: SKILL_LABEL[c.evidenceId] ?? c.evidenceId,
      anchor: `skill:${c.evidenceId}`,
    };
  }
  if (c.targetKind === "axis_weights" || c.targetKind === "axis_threshold") {
    const ref = c.targetRef ?? c.evidenceId;
    const subKind = c.targetKind === "axis_weights" ? "pesos" : "corte";
    return {
      section: "Eixos",
      detail: `${AXIS_LABEL[ref] ?? ref} · ${subKind}`,
      anchor: `axis:${ref}`,
    };
  }
  return { section: "Outro", detail: c.evidenceId, anchor: `other:${c.evidenceId}` };
}

export function ProfileHistoryDrawer({
  open,
  evaluationId,
  candidateName,
  onClose,
}: {
  open: boolean;
  evaluationId: string | null;
  candidateName: string | null;
  onClose: () => void;
}) {
  const evalQuery = useQuery<EvaluationDetail>({
    queryKey: ["labs", "evaluationsGet", evaluationId, "history-detail"],
    queryFn: () => rpcClient.labs.evaluationsGet({ id: evaluationId! }),
    enabled: open && evaluationId !== null,
  });

  const auditQuery = useQuery<AuditRow[]>({
    queryKey: ["labs", "correctionsAuditList", evaluationId, "history"],
    queryFn: () =>
      rpcClient.labs.correctionsAuditList({
        evaluationId: evaluationId ?? undefined,
        limit: 200,
        legacy: true,
      }),
    enabled: open && evaluationId !== null,
  });

  // Newest correction first → genesis at the bottom
  const corrections = useMemo(() => {
    if (!auditQuery.data) return [];
    return auditQuery.data.filter((r) => r.status !== "rejected" && r.status !== "reverted");
  }, [auditQuery.data]);

  // Map evidenceId / skill / axis → its latest correction so the
  // manuscript can render track-changes on the right pane.
  const correctionByAnchor = useMemo(() => {
    const m = new Map<string, AuditRow>();
    // corrections is already newest-first; first occurrence per anchor wins
    for (const c of corrections) {
      const key = labelCorrection(c).anchor;
      if (!m.has(key)) m.set(key, c);
    }
    return m;
  }, [corrections]);

  const [activeAnchor, setActiveAnchor] = useState<string | null>(null);
  const pageRef = useRef<HTMLDivElement | null>(null);

  // Close on Escape, lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || !evaluationId) return null;

  const detail = evalQuery.data;
  const isLoading = evalQuery.isLoading || auditQuery.isLoading;

  function jumpToAnchor(anchor: string) {
    setActiveAnchor(anchor);
    // Defer scroll so the .is-pulse class also re-applies
    requestAnimationFrame(() => {
      const el = pageRef.current?.querySelector<HTMLElement>(`[data-anchor="${anchor}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
    // Clear pulse class after the animation runs (1.6s in CSS)
    window.setTimeout(() => setActiveAnchor((a) => (a === anchor ? null : a)), 1700);
  }

  /** Open the full candidate-intelligence report in a new tab so the
   *  reviewer keeps the history drawer open as a reference. Mirrors the
   *  archive row's `openInReport` so both surfaces feel symmetrical. */
  const openFullReport = () => {
    if (typeof window === "undefined" || !evaluationId) return;
    const url = `/labs/candidate-intelligence?evaluationId=${encodeURIComponent(evaluationId)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    // Click-outside-to-close surface. Was a <button>, but the inner drawer
    // contains buttons + links + the manuscript with its own clickable
    // version pills, which produced a validateDOMNesting warning. A
    // presentational div gives us click-outside without nesting buttons.
    // Esc is wired on the document via the effect above; the explicit
    // close button inside the drawer covers keyboard-only navigation.
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop pattern, redundant with the explicit close button inside
    <div className="history-drawer-overlay" role="presentation" onClick={onClose}>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation is bookkeeping for the backdrop's click handler — this dialog has its own focusable controls */}
      <div
        className="history-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Histórico de ${candidateName ?? "candidato"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="history-drawer-header">
          <div className="history-drawer-header-left">
            <div className="history-drawer-kicker">Histórico de mudanças</div>
            <div className="history-drawer-name">{candidateName ?? "Candidato sem nome"}</div>
          </div>
          <div className="history-drawer-header-actions">
            <button
              type="button"
              className="history-header-btn primary"
              onClick={openFullReport}
              data-testid="history-open-report"
            >
              <span>Abrir relatório completo</span>
              <ExternalLink size={13} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="history-header-btn ghost"
              onClick={onClose}
              aria-label="Fechar"
              data-testid="history-close"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        </header>

        <aside className="history-spine">
          <div className="history-spine-head">
            <div className="history-spine-kicker">Linha do tempo</div>
            <div
              className={`history-spine-count${corrections.length > 0 ? " has-edits" : ""}`}
              data-testid="history-edit-count"
            >
              <span className="history-spine-count-num">{corrections.length}</span>
              <span className="history-spine-count-label">
                {corrections.length === 1 ? "edição" : "edições"}
              </span>
            </div>
          </div>
          <p className="history-spine-sub">
            {corrections.length === 0
              ? "Sem correções — só a avaliação original."
              : "Clique em uma edição para ver onde ela mudou no relatório."}
          </p>

          {isLoading ? (
            <div className="history-loading">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : (
            <ol className="history-spine-list">
              {corrections.map((c, i) => {
                const lbl = labelCorrection(c);
                const isActive = activeAnchor === lbl.anchor;
                const actor = c.correctedBy;
                return (
                  <li
                    key={c.id}
                    className={`history-spine-entry${isActive ? " is-active" : ""}`}
                    style={{ animationDelay: `${Math.min(i * 60, 600)}ms` }}
                  >
                    <button
                      type="button"
                      className="history-spine-entry-btn"
                      onClick={() => jumpToAnchor(lbl.anchor)}
                      data-testid={`history-version-${c.id}`}
                    >
                      <div className="history-spine-meta">
                        <span className="history-spine-date">{formatTimestamp(c.correctedAt)}</span>
                        <span className="history-spine-actor">
                          {actor && actor !== "anonymous" ? (
                            actor
                          ) : (
                            <em
                              className="actor-unknown"
                              title="Avaliação anterior à correção da identidade do autor"
                            >
                              autor desconhecido
                            </em>
                          )}
                        </span>
                      </div>
                      <div className="history-spine-row">
                        <span className="history-spine-target">
                          <span className="target-section">{lbl.section}</span>
                          <span className="target-detail">{lbl.detail}</span>
                        </span>
                        <span className="history-spine-shift">
                          {c.targetKind === "skill_framework" ||
                          c.targetKind === "skill_leveling" ? (
                            <>
                              <DotLevel level={Math.round(Number(c.originalScore))} tone="muted" />
                              <span className="arrow">→</span>
                              <DotLevel level={Math.round(Number(c.correctedScore))} tone="red" />
                            </>
                          ) : (
                            <>
                              <span className="old">{formatScore(c.originalScore)}</span>
                              <span className="arrow">→</span>
                              <span>{formatScore(c.correctedScore)}</span>
                            </>
                          )}
                        </span>
                      </div>
                      {c.rationale && <div className="history-spine-rationale">{c.rationale}</div>}
                    </button>
                  </li>
                );
              })}
              {detail && (
                <li
                  className="history-spine-entry is-genesis"
                  style={{ animationDelay: `${Math.min(corrections.length * 60, 600)}ms` }}
                  data-testid="history-genesis"
                >
                  <div className="history-spine-entry-btn">
                    <div className="history-spine-meta">
                      <span className="history-spine-date">
                        {formatTimestamp(detail.createdAt)}
                      </span>
                      <span className="history-spine-actor">
                        {detail.createdBy && detail.createdBy !== "anonymous" ? (
                          detail.createdBy
                        ) : (
                          <em className="actor-unknown">autor desconhecido</em>
                        )}
                      </span>
                    </div>
                    <div className="history-spine-genesis-label">
                      Avaliação original gerada pelo agente.
                    </div>
                  </div>
                </li>
              )}
            </ol>
          )}
        </aside>

        <main className="history-page" ref={pageRef}>
          {!detail ? (
            <div className="history-loading">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <Manuscript
              detail={detail}
              correctionByAnchor={correctionByAnchor}
              activeAnchor={activeAnchor}
            />
          )}
        </main>
      </div>
    </div>
  );
}

function Manuscript({
  detail,
  correctionByAnchor,
  activeAnchor,
}: {
  detail: EvaluationDetail;
  correctionByAnchor: Map<string, AuditRow>;
  activeAnchor: string | null;
}) {
  const a = detail.assessment;
  const ta = a.threeAxis;
  const decisionLabel = isInterviewDecision(ta?.decision) ? "Entrevistar" : "Não entrevistar";

  // Brief byline — the candidate name lives in the drawer header now,
  // so the manuscript opens straight into the assessment instead of
  // repeating the title.
  const bylineDate = new Date(detail.createdAt).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  const bylineActor =
    detail.createdBy && detail.createdBy !== "anonymous" ? detail.createdBy : null;

  return (
    <>
      <div className="history-page-byline">
        Avaliação registrada em <strong>{bylineDate}</strong>
        {bylineActor ? (
          <>
            {" "}
            por <strong>{bylineActor}</strong>
          </>
        ) : null}
        . Edições aparecem na linha do tempo à esquerda.
      </div>

      <section className="history-section">
        <h3 className="history-section-head">Recomendação</h3>
        <div className="history-decision">
          <span className="new">{decisionLabel}</span>
          <span className="applied-level">{ta?.appliedLevel ?? a.appliedLevel ?? "—"}</span>
        </div>
        {ta?.decisionReason && <p className="history-decision-reason">{ta.decisionReason}</p>}
      </section>

      {ta?.axes && ta.axes.length > 0 && (
        <section className="history-section">
          <h3 className="history-section-head">Eixos</h3>
          {ta.axes.map((axis) => {
            const anchor = `axis:${axis.id}`;
            const c = correctionByAnchor.get(anchor);
            const isActive = activeAnchor === anchor;
            // Axis values are 0–1 thresholds (gca / compWay) or the
            // skills-axis level — keep them numeric / Ln in the
            // manuscript since they're already familiar in that form.
            const newDisplay =
              axis.id === "skills" ? (
                <DotLevel level={axis.value} tone="red" />
              ) : (
                axis.value.toFixed(2)
              );
            const oldDisplay = c ? formatScore(c.originalScore) : null;
            return (
              <ManuscriptRow
                key={axis.id}
                anchor={anchor}
                isActive={isActive}
                isChanged={Boolean(c)}
                label={AXIS_LABEL[axis.id] ?? axis.id}
                sub={
                  axis.id === "skills"
                    ? "exigência de skills"
                    : `corte ${axis.threshold.toFixed(2)}`
                }
                oldDisplay={oldDisplay}
                newDisplay={newDisplay}
                rationale={c?.rationale ?? null}
                rationaleBy={c?.correctedBy ?? null}
              />
            );
          })}
        </section>
      )}

      {ta?.evidences && ta.evidences.length > 0 && (
        <section className="history-section">
          <h3 className="history-section-head">Evidências</h3>
          {ta.evidences.map((ev) => {
            const anchor = `evidence:${ev.id}`;
            const c = correctionByAnchor.get(anchor);
            const isActive = activeAnchor === anchor;
            // Build the row's contextual fold-out:
            //   1) Rubric anchor — the rubric line that matches the
            //      selected level for this evidence. Tells the reader
            //      "this is what the score means".
            //   2) LinkedIn signals — every quote the LLM cited as a
            //      positive (↑) or limiting (↓) signal for this dim,
            //      capped at 6 so a verbose profile doesn't overflow.
            const signals: Signal[] = [];
            const selectedLevel =
              ev.score !== null && Number.isFinite(Number(ev.score))
                ? Math.max(1, Math.min(5, Math.round(Number(ev.score))))
                : null;
            const rubricEntry =
              selectedLevel !== null
                ? ev.rubric?.find((r) => r.level === selectedLevel)
                : undefined;
            if (rubricEntry) {
              signals.push({
                kind: "rubric",
                level: rubricEntry.level,
                description: rubricEntry.description,
              });
            }
            for (const s of ev.sources ?? []) {
              if (!s?.quote || !s?.citation) continue;
              signals.push({ kind: "quote", quote: s.quote, citation: s.citation });
              if (signals.length >= 7) break; // 1 rubric + 6 quotes
            }
            return (
              <ManuscriptRow
                key={ev.id}
                anchor={anchor}
                isActive={isActive}
                isChanged={Boolean(c)}
                label={DIM_LABEL[ev.id] ?? ev.name ?? ev.id}
                sub={null}
                narrative={ev.description ?? null}
                oldDisplay={c ? formatScore(c.originalScore) : null}
                newDisplay={ev.score === null ? "—" : Number(ev.score).toFixed(1)}
                rationale={c?.rationale ?? null}
                rationaleBy={c?.correctedBy ?? null}
                signals={signals}
                signalsHeadline="Como o modelo enxergou essa nota"
              />
            );
          })}
        </section>
      )}

      {ta?.frameworkSkills && ta.frameworkSkills.length > 0 && (
        <section className="history-section">
          <h3 className="history-section-head">Skills</h3>
          {ta.frameworkSkills.map((s) => {
            const anchor = `skill:${s.id}`;
            const c = correctionByAnchor.get(anchor);
            const isActive = activeAnchor === anchor;
            // Skills are dot-rendered everywhere in the candidate page;
            // mirror that here so the history reads in the same visual
            // grammar instead of leaking the L1-L5 internal label.
            const assignedLevel = Number(s.assigned.slice(1));
            const correctedLevel = c ? Math.round(Number(c.correctedScore)) : assignedLevel;
            const oldLevel = c ? Math.round(Number(c.originalScore)) : null;
            const oldDisplay =
              oldLevel !== null ? <DotLevel level={oldLevel} tone="muted" /> : null;
            const newDisplay = <DotLevel level={correctedLevel} tone={c ? "red" : "ink"} />;
            const signals = buildSkillSignals(s.bullets ?? [], assignedLevel);
            return (
              <ManuscriptRow
                key={s.id}
                anchor={anchor}
                isActive={isActive}
                isChanged={Boolean(c)}
                label={SKILL_LABEL[s.id] ?? s.name ?? s.id}
                sub={null}
                oldDisplay={oldDisplay}
                newDisplay={newDisplay}
                rationale={c?.rationale ?? null}
                rationaleBy={c?.correctedBy ?? null}
                signals={signals}
                signalsHeadline="Critérios deste nível e o que o perfil mostra"
              />
            );
          })}
        </section>
      )}

      {a.summary && (
        <section className="history-section">
          <h3 className="history-section-head">Resumo</h3>
          <p className="history-summary">{a.summary}</p>
        </section>
      )}
    </>
  );
}

/** Render a 1–5 dot strip in place of "L3"/"L4" labels. Mirrors the
 *  rubric panel's DotScale on the candidate page so reviewers see the
 *  same visual everywhere. The reverse-half is filled only when
 *  `level` covers it (1 → 1 filled, 5 → all filled). */
function DotLevel({ level, tone = "ink" }: { level: number; tone?: "ink" | "muted" | "red" }) {
  const v = Math.max(0, Math.min(5, Math.round(level)));
  return (
    <span className={`history-dots tone-${tone}`} role="img" aria-label={`Nível ${v} de 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={`history-dot${n <= v ? " on" : ""}`} aria-hidden="true" />
      ))}
    </span>
  );
}

/** The mix of contextual data we surface beneath each row. Each kind
 *  renders slightly differently — rubric anchors, framework criteria
 *  with met/unmet styling, profile quotes with polarity arrows. */
type Signal =
  | { kind: "rubric"; level: number; description: string }
  | {
      kind: "criterion";
      text: string;
      met: boolean;
      quote?: string;
      citation?: string;
      gap?: string;
    }
  | { kind: "quote"; quote: string; citation: string };

/** Strip the LLM's leading ↑/↓ polarity glyph off the quote and surface
 *  it as a styled badge — same treatment QuoteText uses on the candidate
 *  page so positive vs limiting signals read at a glance. */
function PolarizedQuote({ text }: { text: string }) {
  const m = /^\s*([↑↓])\s+([\s\S]*)$/.exec(text);
  if (!m) return <q className="signal-quote">{text}</q>;
  const [, arrow, body] = m;
  const up = arrow === "↑";
  return (
    <q className="signal-quote">
      <span className={`signal-arrow signal-arrow-${up ? "up" : "down"}`} aria-hidden="true">
        {arrow}
      </span>
      {body}
    </q>
  );
}

function SignalsPanel({ signals, headline }: { signals: Signal[]; headline: string }) {
  if (signals.length === 0) return null;
  return (
    <div className="history-row-signals">
      <div className="signals-head">{headline}</div>
      <ul className="signals-list">
        {signals.map((s, i) => {
          if (s.kind === "rubric") {
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: stable per-render
              <li key={i} className="signal signal-rubric">
                <span className="signal-rubric-anchor">
                  <DotLevel level={s.level} tone="ink" />
                  <span className="signal-rubric-label">rubrica neste nível</span>
                </span>
                <span className="signal-rubric-text">{s.description}</span>
              </li>
            );
          }
          if (s.kind === "criterion") {
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: stable per-render
              <li key={i} className={`signal signal-criterion${s.met ? " met" : " miss"}`}>
                <span className="signal-criterion-mark" aria-hidden="true">
                  {s.met ? "✓" : "—"}
                </span>
                <div className="signal-criterion-body">
                  <span className="signal-criterion-text">{s.text}</span>
                  {s.quote && s.citation && (
                    <span className="signal-criterion-evidence">
                      <PolarizedQuote text={s.quote} />
                      <span className="signal-citation">{s.citation}</span>
                    </span>
                  )}
                  {s.gap && <span className="signal-criterion-gap">{s.gap}</span>}
                </div>
              </li>
            );
          }
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: stable per-render
            <li key={i} className="signal signal-quote-row">
              <PolarizedQuote text={s.quote} />
              <span className="signal-citation">{s.citation}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ManuscriptRow({
  anchor,
  isActive,
  isChanged,
  label,
  sub,
  narrative,
  oldDisplay,
  newDisplay,
  rationale,
  rationaleBy,
  signals,
  signalsHeadline,
}: {
  anchor: string;
  isActive: boolean;
  isChanged: boolean;
  label: string;
  sub: string | null;
  /** LLM's plain-language framing of the score for this row — the same
   *  text that renders as `.q` under each evidence row on the candidate
   *  page. Sits below the value, above the rationale/signals. */
  narrative?: string | null;
  /** When the row was corrected: the original value (rendered with strikethrough). */
  oldDisplay: ReactNode | null;
  /** Always rendered — when corrected, in red bold; otherwise as-is. */
  newDisplay: ReactNode;
  rationale: string | null;
  rationaleBy: string | null;
  /** Supporting context for the row — rubric anchor, framework criteria,
   *  LinkedIn quotes. Surfaced under EVERY row (not just corrected
   *  ones) so the reviewer can always answer "what does this score
   *  mean and what backed it?". */
  signals?: Signal[];
  signalsHeadline?: string;
}) {
  const className = `history-row${isChanged ? " is-changed" : ""}${isActive ? " is-pulse" : ""}`;
  return (
    <div className={className} data-anchor={anchor}>
      <div className="history-row-label">
        {label}
        {sub && <span className="sub">{sub}</span>}
      </div>
      <div className="history-row-value">
        {oldDisplay !== null ? (
          <>
            <span className="old">{oldDisplay}</span>
            <span className="arrow">→</span>
            <span className="new">{newDisplay}</span>
          </>
        ) : (
          <span>{newDisplay}</span>
        )}
      </div>
      {narrative && <div className="history-row-narrative">{narrative}</div>}
      {rationale && (
        <div className="history-row-rationale">
          {rationale}
          {rationaleBy && <span className="who">— {rationaleBy}</span>}
        </div>
      )}
      {signals && signals.length > 0 && (
        <SignalsPanel signals={signals} headline={signalsHeadline ?? "Sinais do perfil"} />
      )}
    </div>
  );
}
